<template>
  <v-card outlined>
    <v-card-title>
      Répartition par type de plat
      <dag-info :dags="page.dags" />
    </v-card-title>
    <v-card-subtitle class="d-flex">
      <v-switch
        v-model="page.bySessions"
        hide-details
        class="mt-0 mx-2"
      ></v-switch>
      <span class="text--secondary text-sm switch-label">{{
        page.bySessions === true ? subTitles.bySessions : subTitles.byRecipes
      }}</span>
    </v-card-subtitle>
    <v-card-text>
      <loader v-if="page.loading" />
      <v-container
        v-else-if="chart.options.labels.length === 0"
        class="text-center"
      >
        {{ noDataText }}
      </v-container>
      <vue-apex-charts
        v-show="!page.loading && chart.options.labels.length > 0"
        type="donut"
        height="397"
        :options="chart.options"
        :series="chart.options.series"
        :key="'chart-1' + chart.options.series.length"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from "vue-apexcharts";

import { computed, reactive, watch } from "@vue/composition-api";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import { formatNumber, round2Dec } from "@/utils/formatting";

import DagInfo from "@/components/common/menus/DagInfo";
import Loader from "@/components/common/Loader";
import { ingredientListFromAutoCompleteValues } from "@/components/regie/common/utils/ingredientGroup.js";

import { chartColors, noDataText } from "@/utils/constants";

const subTitles = {
  bySessions: "Par nb. de sessions",
  byRecipes: "Par nb. de recettes",
};

export default {
  name: "RepartitionByDishTypeChart",
  components: {
    DagInfo,
    Loader,
    VueApexCharts,
  },
  setup(props, { emit }) {
    const { axiosGet } = useAxios();
    const { store } = useProxy();

    const page = reactive({
      loading: false,
      forceCall: false,
      bySessions: false,
      subTitle: subTitles.byRecipes,
      dags: [
        "get_and_store_posts_marmiton_ga_daily_dag",
        "adn_daily_posts_sites_infos_dag",
      ],
    });

    const chart = reactive({
      options: {
        legend: {
          show: true,
          position: "bottom",
          fontSize: "14px",
          fontFamily: '"Inter", sans-serif',
        },
        colors: chartColors,
        dataLabels: {
          enabled: true,
        },
        series: [],
        labels: [],
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  fontSize: "2rem",
                  fontFamily: '"Inter", sans-serif',
                },
                value: {
                  fontSize: "1rem",
                  fontFamily: '"Inter", sans-serif',
                  formatter: (val) => {
                    return formatNumber(val, 0);
                  },
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 992,
            options: {
              chart: {
                height: 380,
              },
              legend: {
                position: "bottom",
              },
            },
          },
          {
            breakpoint: 576,
            options: {
              chart: {
                height: 320,
              },
              plotOptions: {
                pie: {
                  donut: {
                    labels: {
                      show: true,
                      name: {
                        fontSize: "1.5rem",
                      },
                      value: {
                        fontSize: "1rem",
                      },
                      total: {
                        fontSize: "1.5rem",
                      },
                    },
                  },
                },
              },
              legend: {
                show: true,
              },
            },
          },
        ],
      },
    });

    // =>  METHODS
    const getData = async () => {
      if (
        (applyFilters.value || page.forceCall) &&
        !page.loading &&
        sites.value &&
        sites.value.length > 0 &&
        startDate.value &&
        startDate.value.length === 10 &&
        endDate.value &&
        endDate.value.length === 10
      ) {
        const params = {
          start_date: startDate.value,
          end_date: endDate.value,
          keywords: searchText.value,
          by_sessions: page.bySessions,
          ...(ingredientsToInclude.value
            ? { ingredients: ingredientsToInclude.value }
            : {}),
          ...(utensilsToInclude.value
            ? { utensils: utensilsToInclude.value }
            : {}),
          ...(flavour.value ? { flavour: flavour.value } : {}),
          ...(dish.value ? { dish: dish.value } : {}),
          ...(cost.value ? { cost: cost.value } : {}),
          ...(duration.value ? { duration: duration.value } : {}),
          ...(rating.value ? { rating: rating.value } : {}),
          ...(rates.value ? { rates: rates.value } : {}),
        };

        try {
          page.loading = true;
          const { data } = await axiosGet(
            "/regie/visitor-info/by-dish-types",
            params
          );

          chart.options.labels = Object.keys(data);
          chart.options.series = Object.values(data).map(round2Dec);

          page.loading = false;
        } catch (err) {
          page.loading = false;
        }

        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }

      page.forceCall = false;
    };

    // =>  COMPUTED
    const applyFilters = computed(
      () => store.getters["common/getApplyFilters"]
    );
    const sites = computed(() => store.getters["marmiton/getSites"]);
    const startDate = computed(() => store.getters["marmiton/getDates"][0]);
    const endDate = computed(() => store.getters["marmiton/getDates"][1]);
    const searchText = computed(
      () => store.getters["marmiton/getContainsText"]
    );

    const flavour = computed(() => store.getters["marmiton/getFlavour"]);
    const dish = computed(() => store.getters["marmiton/getDish"]);
    const cost = computed(() => store.getters["marmiton/getCost"]);
    const duration = computed(() => store.getters["marmiton/getDuration"]);
    const rating = computed(() => store.getters["marmiton/getRating"]);
    const rates = computed(() => store.getters["marmiton/getRates"]);
    const ingredientsToInclude = computed(() => {
      const ingredients =
        store.getters["marmiton/getMarmitonIngredientsToInclude"];

      return ingredientListFromAutoCompleteValues(ingredients).join(",");
    });
    const utensilsToInclude = computed(() =>
      store.getters["marmiton/getMarmitonUtensilsToInclude"].join(",")
    );

    // =>  WATCH
    watch(
      () => page.bySessions,
      (newValue) => {
        if (newValue) {
          page.subTitle = subTitles.bySessions;
        } else {
          page.subTitle = subTitles.byRecipes;
        }

        page.forceCall = chart.options.series.length > 0;
        getData();
      }
    );

    watch(applyFilters, () => {
      getData();
    });

    // =>  HOOKS

    // =>  DATA
    return {
      page,
      chart,
      noDataText,
      subTitles,
    };
  },
};
</script>

<style scoped>
.switch-label {
  margin-top: 0.3rem;
}
</style>
